import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, Download } from "lucide-react";
import { navLinks, personalInfo } from "../../data/mock";
import { Button } from "../ui/button";

const Navbar = ({ darkMode, setDarkMode }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 20);

        const sections = navLinks.map((l) => l.href.replace("#", ""));
        for (let i = sections.length - 1; i >= 0; i--) {
          const el = document.getElementById(sections[i]);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 100) {
              setActiveSection(sections[i]);
              break;
            }
          }
        }
        ticking.current = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = useCallback((e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? darkMode
            ? "bg-[#09090b]/80 backdrop-blur-2xl border-b border-white/[0.06] shadow-lg shadow-black/20"
            : "bg-white/80 backdrop-blur-2xl border-b border-gray-200/60 shadow-lg shadow-gray-100/40"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => handleNavClick(e, "#home")}
          className="flex items-center gap-2.5 group"
        >
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center group-hover:bg-indigo-500/20 group-hover:border-indigo-500/30 transition-all duration-300">
            <span className="text-indigo-400 font-bold text-sm font-mono">A</span>
          </div>
          <span className={`font-semibold text-sm tracking-wide ${
            darkMode ? "text-white/90" : "text-gray-900"
          }`}>
            {personalInfo.firstName}
            <span className="text-indigo-400">.</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-0.5">
          {navLinks.map((link) => {
            const isActive = activeSection === link.href.replace("#", "");
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                aria-current={isActive ? "page" : undefined}
                className={`relative px-3.5 py-2 text-[13px] font-medium transition-colors duration-300 rounded-lg ${
                  isActive
                    ? "text-indigo-400"
                    : darkMode
                    ? "text-white/40 hover:text-white/70"
                    : "text-gray-400 hover:text-gray-700"
                }`}
              >
                {link.label}
                {isActive && (
                  <motion.div
                    layoutId="navIndicator"
                    className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-[2px] w-4 rounded-full bg-indigo-400"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </a>
            );
          })}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDarkMode(!darkMode)}
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            className={`w-8 h-8 rounded-lg transition-all duration-300 ${
              darkMode
                ? "text-white/40 hover:text-white hover:bg-white/[0.06]"
                : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            {darkMode ? <Sun size={15} /> : <Moon size={15} />}
          </Button>

          <a
            href={personalInfo.resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex"
          >
            <Button
              variant="ghost"
              size="sm"
              className="text-[12px] h-8 px-3 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 border border-indigo-500/20 hover:border-indigo-500/30 rounded-lg gap-1.5 transition-all duration-300"
            >
              <Download size={13} />
              Resume
            </Button>
          </a>

          {/* Mobile toggle */}
          <Button
            variant="ghost"
            size="icon"
            className={`md:hidden w-9 h-9 rounded-lg ${
              darkMode ? "text-white/60" : "text-gray-600"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className={`md:hidden border-t overflow-hidden ${
              darkMode
                ? "bg-[#09090b]/95 backdrop-blur-2xl border-white/[0.06]"
                : "bg-white/95 backdrop-blur-2xl border-gray-200/60"
            }`}
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.04 }}
                  className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    activeSection === link.href.replace("#", "")
                      ? "text-indigo-400 bg-indigo-500/10"
                      : darkMode
                      ? "text-white/50 hover:text-white/70 hover:bg-white/[0.04]"
                      : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  {link.label}
                </motion.a>
              ))}
              <a
                href={personalInfo.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2"
              >
                <Button
                  size="sm"
                  className="w-full bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 border border-indigo-500/20 rounded-lg gap-2"
                >
                  <Download size={14} />
                  Download Resume
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
